import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Menu, X } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useMe, useTimeZoneSync } from '@/lib/me';
import { ReadyMark } from './logo';
import { ThemeToggle } from './theme-toggle';
import { Button } from './ui';
import { SiteFooter } from './footer-links';
import { AppShell } from './app-shell/app-shell';

const navClass = ({ isActive }: { isActive: boolean }) =>
  `rounded-md px-3 py-1.5 ${isActive ? 'font-medium text-foreground' : 'text-muted-foreground hover:text-foreground'}`;

const menuItemClass = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-12 items-center rounded-lg px-3 text-base ${isActive ? 'bg-muted font-medium text-foreground' : 'text-foreground hover:bg-muted'}`;

/** Signed in: the app shell. Signed out (public quiz, course, event and verify pages): a simple header. */
export function Layout() {
  const { user } = useAuth();
  useTimeZoneSync();
  return user ? <AppShell /> : <PublicLayout />;
}

function PublicLayout() {
  const { user, signOut } = useAuth();
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // Close the phone menu whenever the page changes.
  useEffect(() => setMenuOpen(false), [pathname]);

  const handleSignOut = async () => {
    await signOut();
    queryClient.clear();
    navigate('/');
  };

  return (
    <div className="flex min-h-dvh flex-col">
      <header className="relative border-b border-border">
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
          <Link
            to={user ? '/home' : '/'}
            className="-my-2 flex items-center gap-2 py-2 font-semibold"
          >
            <ReadyMark className="size-6 text-primary" />
            <span>Nirvik Ready</span>
          </Link>
          <nav className="flex items-center gap-1 text-sm">
            {user ? (
              <>
                {/* Wider screens: links in the bar. */}
                <div className="hidden items-center gap-1 sm:flex">
                  <NavLink to="/home" className={navClass}>
                    Home
                  </NavLink>
                  <NavLink to="/learn" className={navClass}>
                    Explore
                  </NavLink>
                  {me.data?.isPlatformAdmin && (
                    <NavLink to="/admin" className={navClass}>
                      Admin
                    </NavLink>
                  )}
                  <NavLink to="/account" className={navClass}>
                    {user.email}
                  </NavLink>
                  <Button variant="ghost" onClick={handleSignOut}>
                    Sign out
                  </Button>
                </div>
                {/* Phones: one menu button. */}
                <button
                  type="button"
                  className="flex size-11 items-center justify-center rounded-md text-foreground hover:bg-muted sm:hidden"
                  aria-expanded={menuOpen}
                  aria-controls="mobile-menu"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  onClick={() => setMenuOpen((o) => !o)}
                >
                  {menuOpen ? (
                    <X className="size-5" aria-hidden />
                  ) : (
                    <Menu className="size-5" aria-hidden />
                  )}
                </button>
              </>
            ) : (
              <Button onClick={() => navigate('/signin')}>Sign in</Button>
            )}
            <ThemeToggle />
          </nav>
        </div>
        {user && menuOpen && (
          <div
            id="mobile-menu"
            className="absolute inset-x-0 top-full z-20 border-b border-border bg-background px-4 pb-4 pt-2 shadow-lg sm:hidden"
          >
            <p className="truncate px-3 pb-2 text-xs text-muted-foreground">{user.email}</p>
            <div className="space-y-1">
              <NavLink to="/home" className={menuItemClass}>
                Home
              </NavLink>
              <NavLink to="/learn" className={menuItemClass}>
                Explore
              </NavLink>
              <NavLink to="/certificates" className={menuItemClass}>
                Your certificates
              </NavLink>
              <NavLink to="/account" className={menuItemClass}>
                Account
              </NavLink>
              {me.data?.isPlatformAdmin && (
                <NavLink to="/admin" className={menuItemClass}>
                  Platform admin
                </NavLink>
              )}
              <button
                type="button"
                className="flex min-h-12 w-full items-center rounded-lg px-3 text-left text-base text-muted-foreground hover:bg-muted"
                onClick={handleSignOut}
              >
                Sign out
              </button>
            </div>
          </div>
        )}
      </header>
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:py-10">
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}
